/**
 * Histórico de turnos — a conferência, separada da operação.
 *
 * Saiu de dentro de `TelaCaixa.tsx` de propósito. "Quanto tem na gaveta agora"
 * e "o turno de terça bateu" são perguntas de momentos diferentes, e a segunda
 * precisa de filtro, busca e paginação — que empilhados embaixo do painel de
 * operação atrapalhavam quem só queria sangrar.
 *
 * A coluna que importa é a DIFERENÇA. Ela é o motivo de esta lista existir: um
 * histórico de turnos sem a quebra em destaque é uma lista de datas.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Icone } from '../../componentes/Icone'
import { Chips, Paginacao, VazioFiltrado, VazioPrimeiraVez } from '../../componentes/Listagem'
import { Selo } from '../../componentes/Selo'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { carregarHistorico, type HistoricoTurnos, type Turno } from '../../plataforma/caixa'
import { LayoutApp } from '../app/LayoutApp'

const FILTROS = [
  { chave: '', rotulo: 'Todos', sempre: true },
  { chave: 'aberto', rotulo: 'Aberto' },
  { chave: 'fechado', rotulo: 'Fechados' },
]

export function TelaHistoricoTurnos() {
  const navegar = useNavigate()
  const [dados, setDados] = useState<HistoricoTurnos | null>(null)
  const [status, setStatus] = useState('')
  const [busca, setBusca] = useState('')
  const [pagina, setPagina] = useState(1)
  const [carregando, setCarregando] = useState(true)
  const [falha, setFalha] = useState('')

  const buscar = useCallback(
    async (sinal: AbortSignal) => {
      setCarregando(true)
      try {
        const resposta = await carregarHistorico({ status, busca, pagina }, sinal)
        if (sinal.aborted) return
        setDados(resposta)
        setFalha('')
      } catch {
        if (!sinal.aborted) setFalha('Não foi possível carregar o histórico.')
      } finally {
        if (!sinal.aborted) setCarregando(false)
      }
    },
    [status, busca, pagina]
  )

  useEffect(() => {
    const controle = new AbortController()
    void buscar(controle.signal)
    return () => controle.abort()
  }, [buscar])

  const linhas = dados?.linhas ?? []
  const filtrando = status !== '' || busca !== ''

  return (
    <LayoutApp
      titulo="Histórico de turnos"
      subtitulo="Cada linha guarda a contagem, a conferência e a observação"
      acoes={
        <Link
          to="/app/caixa"
          className="flex h-[34px] items-center gap-2 rounded-sistema border border-borda bg-white px-4 text-[13.5px] font-semibold whitespace-nowrap text-marca"
        >
          <Icone nome="storefront" tamanho={18} />
          Turno de hoje
        </Link>
      }
    >
      <div className="flex w-full flex-col gap-4">
        {falha && (
          <div className="rounded-sistema border border-erro-barra bg-erro-fundo px-4 py-3 text-[13px] text-erro-forte">
            {falha}
          </div>
        )}

        <section className="rounded-sistema border border-borda bg-superficie shadow-[var(--shadow-cartao)]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-borda p-4">
            <Chips
              chips={FILTROS}
              valor={status}
              rotuloDoGrupo="Situação do turno"
              aoTrocar={(chave) => {
                setStatus(chave)
                setPagina(1)
              }}
            />

            <div className="composto flex h-[34px] w-full max-w-[280px] items-center gap-2 px-3">
              <Icone nome="search" tamanho={17} className="shrink-0 text-inativo" />
              <input
                type="search"
                placeholder="Turno, operador ou caixa"
                className="w-full bg-transparent text-[13px] outline-none"
                aria-label="Buscar turno"
                value={busca}
                onChange={(e) => {
                  setBusca(e.target.value)
                  setPagina(1)
                }}
              />
            </div>
          </div>

          {carregando && !dados ? (
            <p className="px-5 py-10 text-sm text-texto-suave">Carregando…</p>
          ) : linhas.length === 0 ? (
            filtrando ? (
              <VazioFiltrado
                titulo="Nenhum turno neste recorte"
                descricao="Nenhum turno bate com o filtro e a busca de agora."
                aoLimpar={() => {
                  setStatus('')
                  setBusca('')
                  setPagina(1)
                }}
              />
            ) : (
              <VazioPrimeiraVez
                icone="storefront"
                titulo="Nenhum turno ainda"
                descricao="Quando o primeiro caixa for aberto e fechado, ele aparece aqui com a contagem e a diferença."
              />
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse">
                <caption className="sr-only">Histórico de turnos de caixa</caption>
                <thead>
                  <tr className="bg-fundo">
                    {['Turno', 'Caixa', 'Abertura', 'Operador', 'Situação', 'Contado', 'Diferença'].map(
                      (titulo) => (
                        <th
                          key={titulo}
                          scope="col"
                          className={`border-b border-borda px-3 py-2.5 text-[12.5px] font-semibold text-texto-suave ${
                            titulo === 'Contado' || titulo === 'Diferença'
                              ? 'text-right'
                              : 'text-left'
                          }`}
                        >
                          {titulo}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((turno) => (
                    <tr
                      key={turno.codigo}
                      onClick={() => navegar(`/app/caixa/turnos/${turno.codigo}`)}
                      className="cursor-pointer border-b border-divisoria hover:bg-fundo"
                    >
                      <td className="px-3 py-2.5 text-[13px] font-semibold text-marca">
                        {turno.codigo}
                      </td>
                      <td className="px-3 py-2.5 text-[13px] text-texto">{turno.terminal}</td>
                      <td className="tabular px-3 py-2.5 text-[13px] text-texto-suave">
                        {dataHoraDe(turno.aberto_em)}
                      </td>
                      <td className="px-3 py-2.5 text-[13px] text-texto-suave">
                        {turno.aberto_por || '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        <Selo tom={turno.status === 'aberto' ? 'info' : 'neutro'}>
                          {turno.status === 'aberto' ? 'Aberto' : 'Fechado'}
                        </Selo>
                      </td>
                      <td className="tabular px-3 py-2.5 text-right text-[13px] text-texto">
                        {turno.contado_centavos === undefined
                          ? '—'
                          : formatarMoeda(turno.contado_centavos)}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Diferenca turno={turno} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {dados && dados.total > dados.tamanho && (
            <Paginacao
              pagina={dados.pagina}
              paginas={Math.ceil(dados.total / dados.tamanho)}
              tamanho={dados.tamanho}
              total={dados.total}
              substantivo="turno"
              aoTrocarPagina={setPagina}
            />
          )}
        </section>
      </div>
    </LayoutApp>
  )
}

/**
 * A diferença, com o nome que o lojista usa.
 *
 * "−R$ 40,00" não diz nada sozinho; "Quebra R$ 40,00" diz. E turno que bateu
 * mostra "Bateu", não um zero — zero desenhado é indistinguível de campo vazio.
 */
function Diferenca({ turno }: { turno: Turno }) {
  if (turno.status === 'aberto' || turno.diferenca_centavos === undefined) {
    return <span className="text-[13px] text-texto-fraco">—</span>
  }

  const valor = turno.diferenca_centavos
  if (valor === 0) {
    return <Selo tom="ok">Bateu</Selo>
  }

  return (
    <span
      className={`tabular text-[13px] font-semibold ${valor < 0 ? 'text-erro' : 'text-atencao'}`}
    >
      {valor < 0 ? 'Quebra' : 'Sobra'} {formatarMoeda(Math.abs(valor))}
    </span>
  )
}

function dataHoraDe(iso: string): string {
  const data = new Date(iso)
  return Number.isNaN(data.getTime())
    ? '—'
    : data.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
}
